import React from "react";
import images from "../../../assets/Icons/Icons";
import apiPaths from "../../../api/endpoints";
import { formatDate } from "./../../../utils/formatDate";
import {
  isAssetValid,
  AUDIO_BUFFER_SEC,
  IMAGE_BUFFER_SEC,
} from "../../../utils/assetExpiry";
import useRemintAsset from "../../../hooks/useRemintAsset";
import "./SchoolWork.scss";

const WorkCard = ({
  item,
  showGrade,
}: {
  item: any;
  showGrade?: boolean;
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [audioDuration, setAudioDuration] = React.useState(0);
  const [imageLoadFailed, setImageLoadFailed] = React.useState(false);
  const [audioLoadFailed, setAudioLoadFailed] = React.useState(false);
  const [lightboxImageLoadFailed, setLightboxImageLoadFailed] =
    React.useState(false);
  const [shouldFocusReload, setShouldFocusReload] = React.useState(false);
  const reloadButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // Tracks the active (possibly refreshed) asset path for this card.
  const [resolvedPath, setResolvedPath] = React.useState<string>(
    item.answerContent ?? "",
  );

  // Lightbox open/close for enlarged image view.
  const [imageExpanded, setImageExpanded] = React.useState(false);

  const { remint, loading: isRefreshing } = useRemintAsset();

  const buildSrc = (path: string) => `${apiPaths.CONTENT_BASE_URL}${path}`;

  // ------------------------------------------------------------------
  // Shared helper: refresh the signed token if the asset is about to
  // expire, then call the provided callback with the ready src URL.
  // ------------------------------------------------------------------
  const withFreshToken = React.useCallback(
    async (bufferSec: number, onReady: (src: string) => void) => {
      const currentPath = resolvedPath;

      if (isAssetValid(currentPath, bufferSec)) {
        onReady(buildSrc(currentPath));
        return;
      }

      // Token expired / about to expire — call remint API
      const [freshPath] = await remint([currentPath]);
      const activePath = freshPath ?? currentPath;
      if (freshPath && freshPath !== currentPath) setResolvedPath(freshPath);
      onReady(buildSrc(activePath));
    },
    [resolvedPath, remint],
  );

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------
  const toggleExpand = () => setExpanded((prev) => !prev);

  const handleLoadedMetadata = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    const buffer = audioDuration + AUDIO_BUFFER_SEC;

    withFreshToken(buffer, (src) => {
      if (!audioRef.current) return;
      if (audioRef.current.src !== src) {
        audioRef.current.src = src;
        audioRef.current.load();
      }
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    });
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const { currentTime, duration } = audioRef.current;
    setProgress((currentTime / duration) * 100 || 0);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percentage * audioRef.current.duration;
  };

  // Called when user clicks to enlarge the image.
  const handleImageExpand = () => {
    withFreshToken(IMAGE_BUFFER_SEC, (src) => {
      setResolvedPath(src.replace(apiPaths.CONTENT_BASE_URL, ""));
      setLightboxImageLoadFailed(false);
      setImageExpanded(true);
    });
  };

  const closeLightbox = () => setImageExpanded(false);

  const retryFailedMedia = async (
    target: "image" | "audio" | "lightbox-image",
  ) => {
    if (isRefreshing || !resolvedPath) return;

    const [freshPath] = await remint([resolvedPath]);
    if (freshPath && freshPath !== resolvedPath) setResolvedPath(freshPath);

    if (target === "image") setImageLoadFailed(false);
    if (target === "audio") setAudioLoadFailed(false);
    if (target === "lightbox-image") setLightboxImageLoadFailed(false);
  };

  const renderMediaLoadFallback = (onReload: () => void, lightbox = false) => (
    <div
      className={`media-load-fallback${lightbox ? " media-load-fallback--lightbox" : ""}`}
    >
      <p className="media-load-fallback__message" role="status">
        This media could not be loaded.
      </p>
      <p className="media-load-fallback__hint">Try reloading it.</p>
      <button
        ref={reloadButtonRef}
        className="media-load-fallback__reload"
        onClick={onReload}
        aria-disabled={isRefreshing ? "true" : undefined}
      >
        Reload
      </button>
    </div>
  );

  React.useEffect(() => {
    if (shouldFocusReload && reloadButtonRef.current) {
      reloadButtonRef.current.focus();
      setShouldFocusReload(false);
    }
  }, [shouldFocusReload]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className={`work-card ${expanded ? "expanded" : ""}`}>
      {/* Header */}
      <div className="work-card__header">
        <span className="student-pill">
          {showGrade && item.grade ? `${item.name} • ${item.grade}` : item.name}
        </span>
        {item.isNew && <span className="new-badge">NEW</span>}
      </div>

      {/* ── IMAGE (Doodle / TalkRequest / Dance / Music / etc.) ─────────── */}
      {(item.contentType === "Doodle" ||
        item.contentType === "TalkRequest" ||
        item.contentType === "Dance" ||
        item.contentType === "Music" ||
        item.contentType === "MagicBackPack" ||
        item.contentType === "Journal" ||
        item.contentType === "BreathingExercise" ||
        item.contentType === "Corner") &&
        item.answerType === "image" && (
          <div className="artwork">
            {imageLoadFailed
              ? renderMediaLoadFallback(() => retryFailedMedia("image"))
              : // Small inline preview — no expiry check needed
                (
                  <img
                    src={buildSrc(resolvedPath)}
                    alt={item.image || "Student artwork"}
                    style={{ cursor: "pointer" }}
                    onClick={handleImageExpand}
                    onError={() => setImageLoadFailed(true)}
                    title="Click to enlarge"
                  />
                )}
            {isRefreshing && (
              <span className="asset-refreshing">Refreshing…</span>
            )}
          </div>
        )}

      {/* Lightbox overlay for enlarged image */}
      {imageExpanded && (
        <div
          className="image-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged image"
        >
          <div
            className="image-lightbox__inner"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-lightbox__close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              ✕
            </button>
            {lightboxImageLoadFailed
              ? renderMediaLoadFallback(
                  () => retryFailedMedia("lightbox-image"),
                  true,
                )
              : (
                  <img
                    src={buildSrc(resolvedPath)}
                    alt="Enlarged artwork"
                    onError={() => setLightboxImageLoadFailed(true)}
                  />
                )}
          </div>
        </div>
      )}

      {/* ── JOURNAL (text) ─────────────────────────────────────────────── */}
      {(item.contentType === "Journal" ||
        item.contentType === "Dance" ||
        item.contentType === "Music" ||
        item.contentType === "TalkRequest" ||
        item.contentType === "MagicBackPack" ||
        item.contentType === "Story" ||
        item.contentType === "BreathingExercise" ||
        item.contentType === "Corner") &&
        item.answerType === "text" && (
          <div className="journal">
            <div className="journal-header" onClick={toggleExpand}>
              <span className="journal-icon">
                <img
                  src={images.booklogo}
                  alt="Journal Icon"
                  width={100}
                  height={78}
                />
              </span>
              <span className="journal-title">
                Journal{" "}
                <span className="journal-stitle">
                  entry from <b>{formatDate(item.timestamp)}</b>
                </span>
              </span>
              <span className="chevron">
                <img
                  src={expanded ? images.upIocn : images.downIcon}
                  width={44}
                  height={44}
                />
              </span>
            </div>
            {expanded && (
              <p className="journal-content">{item.answerContent}</p>
            )}
          </div>
        )}

      {/* ── AUDIO ──────────────────────────────────────────────────────── */}
      {(item.contentType === "Story" ||
        item.contentType === "Dance" ||
        item.contentType === "Music" ||
        item.contentType === "TalkRequest" ||
        item.contentType === "Corner" ||
        item.contentType === "BreathingExcercise" ||
        item.contentType === "Journal" ||
        item.contentType === "MagicBackPack") &&
        item.answerType === "audio" && (
          <div className="audio">
            <div className="audio-header" onClick={toggleExpand}>
              <img src={images.audioIcon} alt="Audio" width={100} height={78} />
              <span className="journal-title">
                Journal{" "}
                <span className="journal-stitle">
                  entry from <b>{formatDate(item.timestamp)}</b>
                </span>
              </span>
              <span className="chevron">
                <img
                  src={expanded ? images.upIocn : images.downIcon}
                  alt=""
                  width={44}
                  height={44}
                />
              </span>
            </div>

            {expanded && (
              <>
                <span className="question">{item.question}</span>
                {audioLoadFailed ? (
                  renderMediaLoadFallback(() => retryFailedMedia("audio"))
                ) : (
                  <div className="audio-player-card">
                    <audio
                      ref={audioRef}
                      src={buildSrc(resolvedPath)}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={() => setIsPlaying(false)}
                      onLoadedMetadata={handleLoadedMetadata}
                      onError={(e) => {
                        if (e.currentTarget.parentElement?.contains(document.activeElement)) {
                          setShouldFocusReload(true);
                        }
                        setIsPlaying(false);
                        setAudioLoadFailed(true);
                      }}
                    />
                    <button
                      className="play-btn-card"
                      onClick={toggleAudio}
                      disabled={isRefreshing}
                    >
                      <img
                        src={isPlaying ? images.pauseIcon : images.playIcon}
                        alt="Play"
                        width={40}
                        height={40}
                      />
                    </button>
                    <div className="progCont">
                      <div className="progress-bar" onClick={handleSeek}>
                        <span
                          className="progress"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      {/* ── CHAT ───────────────────────────────────────────────────────── */}
      {item.contentType === "Chat" && (
        <div className="chat">
          <div className="chat-header" onClick={toggleExpand}>
            <img src={images.chatIcon} alt="Chat" width={100} height={78} />
            <span className="chat-title">
              Chat with Troo{"  "}
              <span>
                from <b>{formatDate(item.timestamp)}</b>
              </span>
            </span>
            <span className="chevron">
              <img
                src={expanded ? images.upIocn : images.downIcon}
                width={44}
                height={44}
              />
            </span>
          </div>

          {expanded && item.answerType === "audio" && (
            <>
              <span className="question">{item.question}</span>
              {audioLoadFailed ? (
                renderMediaLoadFallback(() => retryFailedMedia("audio"))
              ) : (
                <div className="audio-player">
                  <audio
                    ref={audioRef}
                    src={buildSrc(resolvedPath)}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={() => setIsPlaying(false)}
                    onLoadedMetadata={handleLoadedMetadata}
                    onError={(e) => {
                      if (e.currentTarget.parentElement?.contains(document.activeElement)) {
                        setShouldFocusReload(true);
                      }
                      setIsPlaying(false);
                      setAudioLoadFailed(true);
                    }}
                  />
                  <button
                    className="play-btn"
                    onClick={toggleAudio}
                    disabled={isRefreshing}
                  >
                    <img
                      src={isPlaying ? images.pauseIcon : images.playIcon}
                      alt="Play"
                      width={40}
                      height={40}
                    />
                  </button>
                  <div className="progCont">
                    <div className="progress-bar" onClick={handleSeek}>
                      <span
                        className="progress"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {expanded && item.answerType === "chat" && (
            <div className="chat-body">
              <div className="chat-row">
                <div className="question">Question Troo asked</div>
                <span className="question">{item.question}</span>
                <div className="answer">{item.answerContent}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkCard;
